import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { inflate } from 'zlib';

import {
  Address,
  Bin,
  Order,
  Setting,
  Shipment,
  YellowBag
} from '../models';

import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';

import {
  READY,
  SHIPPED
} from '../../ngrx/status-types';

import {
  OA,
  RFID,
  SHEETS
} from '../../ngrx/category-types';

import { allAmazonTransactionFields, allBinFields, allShipmentFields, allYellowBagFields } from '../../ngrx/graphql';
import { stateCodes } from '../../ngrx/state-codes';
import { fromAddress, NEXT_BUSINESS_DAY } from '../../ngrx/shipment-options';
import { SET_SHIPMENT_DETAILS_VIEW } from 'ngrx/actions';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class ShipmentService {
  shipments             : Shipment[];
  orders                : Order[];
  boxingStationPrompt   : Subject<any> = new Subject();
  displayShipmentDetails: Subject<any> = new Subject();
  openIntlInvoiceModal  : Subject<any> = new Subject();
  printInvoice          : Subject<any> = new Subject();
  processEOD            : Subject<any> = new Subject();
  systemSettingList     : Observable<Setting[]>;
  shipmentSubscription  : Subscription;
  orderSubscription     : Subscription;

  constructor(
    private authService: AuthenticationService,
    private credentialService: CredentialService,
    private store: Store<any>
  ) {
    this.systemSettingList = this.store.select('settings', 'systemSettingList');
  }

  private invokeShipments(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.shipmentFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
            if(output.gzip){
              const options = {
                windowBits : 15
              };
              inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                if (err) {                    
                  observer.error(err.stack);
                } else {
                  const unzippedOutput = Object.assign({}, output, {
                    body: data.toString()
                  });
                  observer.next(unzippedOutput);
                }
              });
            } else {
              observer.next(output);
            }
          }
        }
      );
    });
  }

  private invokeShipmentMutation(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.shipmentFunction,
      Payload: JSON.stringify(payload)
      }, (err, data) => {
        // console.log("DATA for invoke shipment mutation: ",data);
        // console.log("err: ",err);
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
        }
      }
    );
  }

  private invokeAmazonTransaction(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.shipmentFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              : data.Payload;
            observer.next(output);
          }
        }
      );
    });
  }

  private invokeCheckAmazonTransaction(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.checkAmazonTransactionFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          console.log('err : ', err);
          console.log('data : ', data);

          if (err) {
            console.log(err);
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              : data.Payload;
            observer.next(output);
          }
        }
      );
    });
  }

  private invokeDeliveryEstimates(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.shipmentDeliveryEstimatesFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              : data.Payload;
            observer.next(output);
          }
        }
      );
    });
  }

  private invokeShippingSpeedAdjustment(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.shipmentSpeedAdjustmentFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          // console.log("invokeShippingSpeedAdjustment: ", data)
          // console.log("invokeShippingSpeedAdjustment err: ", err)
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              : data.Payload;
            observer.next(output);
          }
        }
      );
    });
  }

  private invokeTrackShipment(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.shipmentTrackFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              : data.Payload;
            observer.next(output);
          }
        });
    });
  }

  private invokeValidateAddress(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.shipmentValidateAddressFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              : data.Payload;
            observer.next(output);
          }
        }
      );
    });
  }

  private invokeVoidShipment(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.shipmentVoidFunction,
      Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
          }
        }
      }
    )
  }

  adjustShippingSpeed(shipment: Shipment): Observable<any> {
    console.log(`attempting to adjust shipping speed for shipment: ${JSON.stringify(shipment, null, 2)}`);
    return this.invokeShippingSpeedAdjustment({ shipment });
  }

  createBins(category: string): void {
    const variables = { category };
    const query = `mutation($category: String) {
      createBins(category: $category) {
        ${allBinFields}
      }
    }`;

    this.invokeShipmentMutation({ query, variables });
  }

  createShipmentAndUpdateBin(shipment: Shipment, bin: Bin): void {
    const variables = { shipment, bin };
    const query = `mutation($shipment: ShipmentInput!, $bin: BinInput!) {
      createShipment(shipment: $shipment) {
        ${allShipmentFields}
      }

      updateBin(bin: $bin) {
        ${allBinFields}
      }
    }`

    this.invokeShipmentMutation({ query, variables });
  }

  createYellowBag(yellowBag: YellowBag): void {
    const variables = { yellowBag };
    const query = `mutation ($yellowBag: YellowBagInput!) {
      createYellowBag(yellowBag: $yellowBag) {
        ${allYellowBagFields}
      }
    }`;

    this.invokeShipmentMutation({ query, variables });
  }

  deleteShipment(id: string): void {
    const variables = { id };
    const query = `mutation deleteShipment($id: ID!) {
      deleteShipment(id: $id) {
        id
      }
    }`;

    this.invokeShipmentMutation({ query, variables });
  }

  deleteYellowBag(id: string): void {
    const variables = { id };
    const query = `mutation deleteYellowBag($id: ID!) {
      deleteYellowBag(id: $id) {
        id
      }
    }`;

    this.invokeShipmentMutation({ query, variables });
  }

  formatShipment(shipment: any): Shipment {
    shipment.activity_history = shipment.activity_history.filter(el => {return el != null;});
    const formattedShipment = Object.assign({}, shipment);

    if (shipment.hasOwnProperty('daysUntilDue')) delete formattedShipment.daysUntilDue;
    if (shipment.hasOwnProperty('ttl')) delete formattedShipment.ttl;
    if (shipment.hasOwnProperty('daysUntilDueMsg')) delete formattedShipment.daysUntilDueMsg;
    if (shipment.hasOwnProperty('diffNBD')) delete formattedShipment.diffNBD;

    return formattedShipment;
  }

  formatYellowBag(yellowBag: any): YellowBag {
    const formattedYellowBag = Object.assign({}, yellowBag);

    if (yellowBag.hasOwnProperty('daysUntilDue')) delete formattedYellowBag.daysUntilDue;
    if (yellowBag.hasOwnProperty('ttl')) delete formattedYellowBag.ttl;
    if (yellowBag.hasOwnProperty('daysUntilDueMsg')) delete formattedYellowBag.daysUntilDueMsg;
    if (yellowBag.hasOwnProperty('diffNBD')) delete formattedYellowBag.diffNBD;

    return formattedYellowBag;
  }

  getAll(): Observable<any> {
    const query = `{
      bins(param: "category", value: "${SHEETS}") {
        ${allBinFields}
      }

      activeShipments(category: "${SHEETS}") {
        ${allShipmentFields}
      }

      yellowBags {
        ${allYellowBagFields}
      }
    }`;

    return this.invokeShipments({ query });
  }

  getAllOA(): Observable<any> {
    const query = `{
      bins(param: "category", value: "${OA}") {
        ${allBinFields}
      }

      activeShipments(category: "${OA}") {
        ${allShipmentFields}
      }

      yellowBags {
        ${allYellowBagFields}
      }
    }`;
    
    return this.invokeShipments({ query });
  }

  getAllRFID(): Observable<any> {
    const query = `{
      bins(param: "category", value: "${RFID}") {
        ${allBinFields}
      }

      activeShipments(category: "${RFID}") {
        ${allShipmentFields}
      }
    }`;

    return this.invokeShipments({ query });
  }


  getBin(id: string): Observable<any> {
    const query = `{
      bin(id: "${id}") {
        ${allBinFields}
      }
    }`;

    return this.invokeShipments({ query });
  }

  getBinList(): Observable<Bin[]> {
    return this.store.select('bins', 'binList');
  }

  getBinListView(): Observable<any> {
    return this.store.select('bins', 'binsView');
  }

  getBins(query?: string): Observable<any> {
    const returnAllFields = `{
      bins(param: "category", value: "${SHEETS}") {
        ${allBinFields}
      }
    }`;

    return this.invokeShipments({ query: !query ? returnAllFields : query });
  }

  getBinsAndShipments(query?: string): Observable<any> {
    const returnAllFields = `{
      bins(param: "category", value: "${SHEETS}") {
        ${allBinFields}
      }

      shipments(param: "category", value: "${SHEETS}") {
        ${allShipmentFields}
      }
    }`;

    return this.invokeShipments({ query: !query ? returnAllFields : query });
  }

  getPackageForms(): Observable<any> {
    return this.store.select('shipments', 'packageForms');
  }

  getShipment(id: string, query?: string): Observable<any> {
    const returnAllFields = `{
      shipment(id: "${id}") {
        ${allShipmentFields}
      }
    }`;

    return this.invokeShipments({ query: !query ? returnAllFields : query });
  }

  getShipmentsByID(ids: string[]): Observable<any> {
    let formattedIDs = '';
    ids.forEach(id => {
      formattedIDs += `"${id}", `;
    });

    const query = `{
      shipmentsByID(ids: [${formattedIDs.trim()}]) {
        ${allShipmentFields}
      }
    }`;

    return this.invokeShipments({ query });
  }

  getShipmentsByIDAndBin(shipmentIDs: string[], binID: string): Observable<any> {
    let formattedIDs = '';
    shipmentIDs.forEach(id => {
      formattedIDs += `"${id}", `;
    });

    const query = `{
      bin(id: "${binID}") {
        ${allBinFields}
      }

      shipmentsByID(ids: [${formattedIDs.trim()}]) {
        ${allShipmentFields}
      }
    }`;

    return this.invokeShipments({ query });
  }
  
  getShipmentByOrderID(orderId: string, query?: string): Observable<any> {
    const returnAllFields = `{
      shipmentByOrderID(id: "${orderId}") {
        ${allShipmentFields}
      }
    }`;

    return this.invokeShipments({ query: !query ? returnAllFields : query });
  }

  getRTSTotal(): Observable<any> {
    return this.store.select('shipments', 'RTSTotal');
  }

  getShipmentDetailsView(): Observable<any> {
    return this.store.select('shipments', 'shipmentDetailsView');
  }


  getShipmentList(): Observable<Shipment[]> {
    return this.store.select('shipments', 'shipmentList');
  }

  getShipmentListView(): Observable<any> {
    return this.store.select('shipments', 'shipmentListView');
  }

  getShippingEstimatesList(): Observable<any> {
    return this.store.select('shipments', 'shipmentEstimates');
  }

  getYellowBagList(): Observable<YellowBag[]> {
    return this.store.select('shipments', 'yellowBagList');
  }

  // getShipmentList(status: string, query?: string): Observable<any> {
  //   const returnAllFields = `{
  //     shipments(param: "category", value: "${SHEETS}") {
  //       ${allShipmentFields}
  //     }
  //   }`;

  //   return this.invokeShipments({ query: !query ? returnAllFields : query });
  // }

  async getShippingEstimates(shipment: Shipment, order: Order): Promise<any> {
    try {
      const settings = await this.systemSettingList.take(1).toPromise();
      const { value: nextBusinessDay } = settings.find(setting => setting.name.includes(NEXT_BUSINESS_DAY));

      console.log(`next business day set to: ${nextBusinessDay}`);

      const payload = {
        data: {
          from: {
            city        : fromAddress.city,
            state_code  : fromAddress.state_code,
            postal_code : fromAddress.postal_code,
            country_code: fromAddress.country_code
          },
          to: {
            city        : shipment.address.city,
            state_code  : stateCodes[shipment.address.region.trim().toLowerCase()] || shipment.address.region,
            postal_code : shipment.address.postcode,
            country_code: shipment.address.country_id
          },
          weight        : shipment.totalWeight,
          pickup_date   : (<string>nextBusinessDay),
          total_packages: shipment.packages.length,
          value         : order.totalPrice
        },
        carrier: shipment.carrier
      };

      console.log(`shipment estimates request body: ${JSON.stringify(payload, null, 2)}`);

      return await this.invokeDeliveryEstimates(payload).take(1).toPromise();
    } catch (err) {
      console.log(`Shipment estimate retrieval error: ${JSON.stringify(err)}`);
      return { errorMessage: `Shipment estimate retrieval error: ${JSON.stringify(err)}` };
    }
  }

  getYellowBag(id: string): Observable<any> {
    const query = `{
      yellowBag(id: "${id}") {
        ${allYellowBagFields}
      }
    }`;

    return this.invokeShipments({ query });
  }

  getYellowBags(): Observable<any> {
    const query = `{
      yellowBags {
        ${allYellowBagFields}
      }
    }`;

    return this.invokeShipments({ query });
  }

  qaCheckOK(shipment: Shipment): void {
    const updatedShipment = Object.assign({}, shipment, {
      shipmentStatus: READY
    });

    this.store.dispatch({
      type: SET_SHIPMENT_DETAILS_VIEW,
      payload: { loading: true }
    });

    console.log(`setting shipment to QAOK`);
    this.updateShipment(updatedShipment);
  }

  accessoryCheckOK(shipment: Shipment): void {
    const updatedShipment = Object.assign({}, shipment, {
      accessory: true
    });

    this.store.dispatch({
      type: SET_SHIPMENT_DETAILS_VIEW,
      payload: { loading: true }
    });

    console.log(`setting shipment accessory flag`);
    this.updateShipment(updatedShipment);
  }

  runShipmentEOD(shipmentList: Shipment[], query?: string): Observable<any> {
    // Runs EOD for all shipments that are currently in 'SHIPPED' status
    const shipments = shipmentList.filter(shipment => shipment.shipmentStatus === SHIPPED)
      .map(shipment => {
        const EODShipment = Object.assign({}, shipment);

        delete EODShipment['amountShipped'];
        delete EODShipment['daysUntilDue'];
        delete EODShipment['daysUntilDueMsg'];
        delete EODShipment['progress'];
        delete EODShipment['lastUpdated'];
        delete EODShipment['jobOnHold'];
        delete EODShipment['totalJobs'];

        return EODShipment;
      });

    const variables = { shipments };
    const returnAllFields = `mutation runShipmentEOD($shipments: [ShipmentInput]) {
      runShipmentEOD(shipments: $shipments) {
        ${allShipmentFields}
      }
    }`;

    return this.invokeShipments({
      query: !query ? returnAllFields : query,
      variables
    });
  }

  addShipmentToEOD(shipmentIdList: string[], query?: string): void {
    const variables = { ids: shipmentIdList };
    const returnAllFields = `mutation addShipmentToEOD($ids: [String]) {
      addShipmentToEOD(ids: $ids) {
        ${allShipmentFields}
      }
    }`;

    console.log(returnAllFields)
    this.invokeShipmentMutation({
      query: !query ? returnAllFields : query,
      variables
    });
  }

  trackShipment(shipment: Shipment): Observable<any> {
    return this.invokeTrackShipment({ trackingNumber: (shipment.trackingNumberUPS || shipment.trackingNumberUSPS || shipment.trackingNumberFEDEX), carrier: shipment.carrier });
  }

  updateShipment(shipment: Shipment, query?: string): void {
    const formattedShipment = this.formatShipment(shipment);
    console.log(`Updating shipment: ${JSON.stringify(formattedShipment, null, 2)}`);
    if (formattedShipment) {
      const variables = { shipment: formattedShipment };
      const returnAllFields = `mutation ($shipment: ShipmentInput!) {
        updateShipment(shipment: $shipment) {
          ${allShipmentFields}
        }
      }`;

      this.invokeShipmentMutation({
        query: !query ? returnAllFields : query,
        variables
      });
    }
  }

  updateShipmentAndBin(shipment: Shipment, bin: any): void {
    const formattedShipment = this.formatShipment(shipment);
    console.log(`updating shipment: ${JSON.stringify(formattedShipment, null, 2)}`);
    console.log(`updating bin: ${JSON.stringify(bin, null, 2)}`);
    if (formattedShipment && bin) {
      const variables = { shipment: formattedShipment, bins: bin };
      const query = `mutation($shipment: ShipmentInput!, $bins: [BinInput!]) {
        updateShipment(shipment: $shipment) {
          ${allShipmentFields}
        }

        updateBins(bins: $bins) {
          ${allBinFields}
        }
      }`;

      this.invokeShipmentMutation({ query, variables });
    }
  }

  updateYellowBag(yellowBag: YellowBag): void {
    const formattedYellowBag = this.formatYellowBag(yellowBag);
    console.log(`updating yellow bag: ${JSON.stringify(formattedYellowBag, null, 2)}`);
    if (formattedYellowBag) {
      const variables = { yellowBag: formattedYellowBag };
      const query = `mutation($yellowBag: YellowBagInput!) {
        updateYellowBag(yellowBag: $yellowBag) {
          ${allYellowBagFields}
        }
      }`;

      this.invokeShipmentMutation({ query, variables });
    }
  }

  async updateAmazonTransaction(transaction) {
    // const formattedYellowBag = this.formatYellowBag(yellowBag);
    const formattedtransaction = Object.assign({}, transaction);
    
    console.log(`utransaction bag: ${JSON.stringify(formattedtransaction, null, 2)}`);
    if (transaction) {
      const variables = { transaction: formattedtransaction };
      const query = `mutation($transaction: AmazonTransactionInput!) {
        updateAmazonTransactionStatus(amazonTransaction: $transaction) {
          ${allAmazonTransactionFields}
        }
      }`;

      return await this.invokeAmazonTransaction({ query, variables }).take(1).toPromise();
    }
  }

  async getAmazonTransaction(id) {
    if (id) {
      const query = `{
        getAmazonTransaction(id: "${id}") {
          ${allAmazonTransactionFields}
        }
      }`;

      return this.invokeShipments({ query }).take(1).toPromise();
    }
  }

  async checkAmazonTransaction(poNumber) {
      return await this.invokeCheckAmazonTransaction({  id : poNumber  }).take(1).toPromise();
  }
  /*
  private updateShipmentRequest(shipment: Shipment, jobs: Job[]): void {
    const jobNotInShippedStatus = !!jobs.find(job => job.status !== SHIPMENTS);
    const updateToReady = !jobNotInShippedStatus && shipment.shipmentStatus === NEW;
    const updateToNew = !!jobNotInShippedStatus && shipment.shipmentStatus === READY;
    const shipmentData = Object.assign({}, shipment);

    if (updateToNew) {
      console.log(`shipment ${shipmentData.id} status now set to: New`);
      shipmentData.shipmentStatus = NEW;
    } else if (updateToReady) {
      console.log(`shipment ${shipmentData.id} status now set to: Ready`);
      shipmentData.shipmentStatus = READY;
    }

    // let bin;

    // if (updateToReady && shipmentData.bin) {
    //   shipmentData.bin = null;
    //   bin = {
    //     id: shipment.bin,
    //     orderID: null,
    //     shipmentID: null
    //   }
    // }

    if (shipmentData.shipmentStatus !== shipment.shipmentStatus) {
      this.updateShipment(shipmentData);
      // this.updateShipmentAndBin(shipmentData, bin)
    }
  } */

  validateAddress(address: Address, service : string): Observable<any> {
    const formattedAddress = {
      service,
      request_option: 3,
      name          : `${(address.firstname && address.firstname.trim()) || ''} ${(address.lastname && address.lastname.trim()) || ''}`.trim(),
      company       : (address.company && address.company.trim()) || '',
      address_lines: [
      (address.street && address.street.trim()) || '',
      (address.street2 && address.street2.trim()) || '',
      (address.street3 && address.street3.trim()) || ''
      ],
      city          : (address.city && address.city.trim()) || '',
      state_code    : stateCodes[address.region.trim().toLowerCase()] || address.region.toLowerCase(),
      postal_code   : (address.postcode && address.postcode.trim()) || '',
      country_code  : (address.country_id && address.country_id.trim()) || ''
    };

    console.log(formattedAddress);

    return this.invokeValidateAddress(formattedAddress);
  }

  voidShipment(shipment: Shipment): void {
    if (!shipment) return;

    console.log(`Attempting to void tracking number ${shipment.trackingNumberUPS || shipment.trackingNumberUSPS || shipment.trackingNumberFEDEX}`);
    this.invokeVoidShipment(shipment);
  }

  getShipmentPromise(id : string): Promise<any> {
    return new Promise( (resolve, reject) => {
      this.getShipment(id)
      .take(1)
      .subscribe((shipment) => {
        const { statusCode, errorMessage } = shipment;
        const shipmentData = JSON.parse(shipment.body);
        //console.log(shipmentData);

        if(statusCode === 400 || errorMessage){
          reject(shipmentData.errors[0].message);
        }else{
          resolve(shipmentData.data.shipment);
        }
      },
      (err : Error) => {
        reject(err);
      })
    })
  }

  getStrDifference = (s1, s2) => {
    const a1 = s1.split(' '), a2 = s2.split(' ')
    // case-insensitive
    let res = a1.reduce((diff, word, pos) => {
      (word && word.toLowerCase()) != (a2[pos] && a2[pos].toLowerCase())
        && diff.push(word)
      return diff
    },
      [])
    return res.join(' ')
  }
}


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/services/shipment.service.ts